import { Box } from "@mui/material";
import { useState } from "react";
import AdjustmentsPanel from "@/components/finance/AdjustmentsPanel";
import ClientPurchasesPanel from "@/components/finance/ClientPurchasesPanel";
import FinanceInvoicesTab from "@/components/finance/FinanceInvoicesTab";
import FinanceRefundsPanel from "@/components/finance/FinanceRefundsPanel";
import { usePermissions } from "@/hooks/usePermissions";

type FinanceTab = "invoices" | "purchases" | "refunds" | "adjustments";

function TabButton({
  active,
  label,
  onClick,
}: {
  active: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      style={{
        appearance: "none",
        background: "none",
        border: "none",
        cursor: "pointer",
        padding: "10px 4px",
        marginBottom: -1,
        fontFamily: "var(--font-lato)",
        fontSize: 14,
        fontWeight: 600,
        letterSpacing: "0.2px",
        color: active ? "var(--color-rodeo-black)" : "var(--color-graphite)",
        borderBottom: `2px solid ${active ? "var(--color-dark-dust)" : "transparent"}`,
        transition: "all 0.15s",
      }}
    >
      {label}
    </button>
  );
}

/** GFG Finance console. Invoices is visible to any CREDIT/view holder (row-scoped for clients);
 *  Refunds is a GFG-only reversal audit (FINANCE/view), so its tab only appears for GFG. */
export default function Finance() {
  const { can } = usePermissions();
  const canSeeRefunds = can("view", "FINANCE");
  // Adjustments is gated on the RAISE capability, not FINANCE: an approver who cannot raise still
  // needs the audit list, and both GFG roles that can approve can also raise.
  const canSeeAdjustments = can("adjust", "CREDIT");
  const [tab, setTab] = useState<FinanceTab>("invoices");

  const tabs: { key: FinanceTab; label: string }[] = [
    { key: "invoices", label: "Invoices" },
    // Client Purchases and Refunds are both GFG-only finance surfaces (view/FINANCE, scope all).
    // Clients see their own money-in through Group › Transactions.
    ...(canSeeRefunds ? [{ key: "purchases" as const, label: "Client Purchases" }] : []),
    ...(canSeeRefunds ? [{ key: "refunds" as const, label: "Refunds" }] : []),
    ...(canSeeAdjustments ? [{ key: "adjustments" as const, label: "Adjustments" }] : []),
  ];

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, fontFamily: "var(--font-lato)" }}>
      <div
        role="tablist"
        style={{
          display: "flex",
          gap: 28,
          borderBottom: "1px solid var(--color-card-border)",
          marginBottom: 24,
        }}
      >
        {tabs.map((t) => (
          <TabButton
            key={t.key}
            active={tab === t.key}
            label={t.label}
            onClick={() => setTab(t.key)}
          />
        ))}
      </div>

      {tab === "invoices" && <FinanceInvoicesTab />}
      {tab === "purchases" && canSeeRefunds && <ClientPurchasesPanel />}
      {tab === "refunds" && canSeeRefunds && <FinanceRefundsPanel />}
      {tab === "adjustments" && canSeeAdjustments && <AdjustmentsPanel />}
    </Box>
  );
}
